// Copyright ©️ 2026 eVolve MEP, LLC

import { useState } from 'react';

import { useForm } from '@mantine/form';

import { loginAnimationDuration } from '../components/AuthFormWrapper';
import type { LoginErrorCode } from '../components/LoginErrorAlert';
import { buildSsoStateVar, type SsoRedirectState } from '../Login/helper';
import { useGetAuthenticationUrl } from './restCalls';

type SignInStep = 'ask-email' | 'transitioning-to-password' | 'ask-password' | 'transitioning-to-email';

export type SignInFormValues = {
  email: string;
  password: string;
};

type Props = {
  initialEmail?: string;
  /** Carried through the SSO round trip when the email belongs to an SSO user. */
  ssoState: SsoRedirectState;
};

/**
 * The email → password sign-in steps shared by the website's login and the desktop sign-in:
 * the form, the animated transitions between steps, and the email lookup that sends SSO users
 * to their identity provider.
 */
export const useSignInSteps = ({ initialEmail, ssoState }: Props) => {
  const { getAuthenticationUrl, loading: lookingUpEmail } = useGetAuthenticationUrl();
  const [step, setStep] = useState<SignInStep>('ask-email');
  const [redirectingToSso, setRedirectingToSso] = useState(false);

  const form = useForm<SignInFormValues>({
    initialValues: {
      email: initialEmail ?? '',
      password: '',
    },
    validate: {
      email: (value) => !value.trim(),
      password: (value) => step !== 'ask-email' && !value,
    },
  });

  const transitionTo = (transitioning: SignInStep, settled: SignInStep) => {
    setStep(transitioning);
    setTimeout(() => setStep(settled), loginAnimationDuration);
  };

  /** Redirects SSO users to their identity provider, or moves to the password step. Resolves with an error code on failure. */
  const submitEmail = async ({ email }: SignInFormValues): Promise<LoginErrorCode | undefined> => {
    try {
      const urlResponse = await getAuthenticationUrl(`email/${email}`);
      const destinationUrl = urlResponse?.returnUrl;
      if (destinationUrl?.startsWith('https://')) {
        setRedirectingToSso(true);
        window.location.href = `${destinationUrl}&state=${buildSsoStateVar(ssoState)}`;
        return undefined;
      }
      form.setFieldValue('password', '');
      transitionTo('transitioning-to-password', 'ask-password');
      return undefined;
    } catch (e) {
      return e === 'Not Found' ? 'UserNotFoundException' : 'UnknownException';
    }
  };

  const settled = step === 'ask-email' || step === 'ask-password';

  return {
    form,
    showPasswordStep: step === 'ask-password' || step === 'transitioning-to-email',
    submittingEmail: lookingUpEmail || redirectingToSso || step === 'transitioning-to-password',
    submitEmail,
    goToEmail: () => transitionTo('transitioning-to-email', 'ask-email'),
    /** Pass to `AuthFormWrapper`'s `boxProps` to slide the current step in and out. */
    stepBoxProps: {
      style: {
        opacity: settled ? 1 : 0,
        marginLeft: settled ? 0 : -50,
        transition: `all ${loginAnimationDuration}ms ease`,
      },
    },
  };
};
